import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import {
    populateAwsDataService,
    populateAzureDataService,
    populateGcpDataService,
    populateOnpremDataService,
    addCloudAccountService,
    getCloudAccountsOverviewService,
    editCloudAccountService,
    rescanCloudAccountService
} from '../../services';
import '../../styles/dashboardV2.scss';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ModuleIcon from '../../Components/ModuleIcon';

const integrations = [
    { name: 'AWS', color: '#FF9900', img: 'https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/aws.svg', type: 'aws' },
    { name: 'Azure', color: '#0078D4', img: 'https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/microsoft-azure.svg', type: 'azure' },
    { name: 'GCP', color: '#4285F4', img: 'https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/googlecloud.svg', type: 'gcp' },
    { name: 'CrowdStrike', color: '#EF4444', icon: '⛨', type: 'crowdstrike' },
    { name: 'Splunk', color: '#8B5CF6', icon: '⌕', type: 'splunk' },
    { name: 'Jira', color: '#10B981', icon: '⊟', type: 'jira' },
    { name: 'Okta', color: '#3B82F6', icon: '◎', type: 'okta' },
    { name: 'Tenable', color: '#F59E0B', icon: '✺', type: 'tenable' },
    { name: 'Qualys', color: '#64748B', icon: '⛨', type: 'qualys' },
    { name: 'SentinelOne', color: '#9F1239', icon: '◐', type: 'sentinelone' },
    { name: 'ServiceNow', color: '#3B82F6', icon: '📄', type: 'servicenow' },
    { name: '80+ more', color: '#A855F7', icon: '✧' },
];

const cloudFields = {
    aws: [
        { key: 'arn', label: 'AWS ARN', placeholder: 'Enter AWS ARN for role-based authentication', type: 'text' },
    ],
    azure: [
        { key: 'tenant_id', label: 'Tenant ID', placeholder: 'Enter Azure Tenant ID', type: 'text' },
        { key: 'client_id', label: 'Client ID', placeholder: 'Enter Azure Client ID (Application ID)', type: 'text' },
        { key: 'client_secret', label: 'Client Secret', placeholder: 'Enter Azure Client Secret', type: 'password' },
        { key: 'subscription_id', label: 'Subscription ID', placeholder: 'Enter Azure Subscription ID', type: 'text' },
    ],
    gcp: [
        { key: 'project_id', label: 'Project ID', placeholder: 'Enter GCP Project ID', type: 'text' },
        { key: 'target_service_account', label: 'Service Account Email', placeholder: 'Enter service account email to impersonate', type: 'text' },
    ],
    onprem: [
        { key: 'excel_file', label: 'Excel File', placeholder: 'Upload Excel file with on-premises assets', type: 'file' },
    ],
};

const cloudTitles = {
    aws: 'Amazon Web Service',
    azure: 'Microsoft Azure',
    gcp: 'Google Cloud',
    onprem: 'On-Premises',
};

function IntegrationCloud({ wrapper = true }) {
    const navigate = useNavigate();
    const [cloudModal, setCloudModal] = useState({ show: false, type: '' });
    const [cloudForm, setCloudForm] = useState({});
    const [cloudLoading, setCloudLoading] = useState(false);
    const [cloudMsg, setCloudMsg] = useState({ success: '', error: '' });
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
    };

    // Cloud Accounts Overview state
    const [cloudAccounts, setCloudAccounts] = useState([]);
    const [fetchingOverview, setFetchingOverview] = useState(false);

    const fetchOverview = async () => {
        setFetchingOverview(true);
        try {
            const response = await getCloudAccountsOverviewService();
            console.log(response, 'response');
            // Nested data structure as per user's snippet: response_data.data
            const data = response?.statusCode?.response_data?.data || [];
            if (Array.isArray(data)) {
                setCloudAccounts(data);
            }
        } catch (error) {
            console.error('Error fetching cloud accounts overview:', error);
        } finally {
            setFetchingOverview(false);
        }
    };

    useEffect(() => {
        fetchOverview();
    }, []);

    // Polling logic: refresh every 2 mins if any account is "running"
    useEffect(() => {
        const hasRunning = cloudAccounts.some(acc => acc.status?.toLowerCase() === 'running');
        let interval;

        if (hasRunning) {
            interval = setInterval(() => {
                fetchOverview();
            }, 120000); // 2 minutes
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [cloudAccounts]);

    const openCloudModal = (type, account = null) => {
        if (account) {
            setCloudForm({
                arn: account.role_arn || '',
                tenant_id: account.tenant_id || '',
                client_id: account.client_id || '',
                subscription_id: account.subscription_id || '',
                project_id: account.project_id || '',
                target_service_account: account.target_service_account || '',
                // If the account has use_external_id field we can pre-fill it too if API provides it
                use_external_id: account.use_external_id || false
            });
            setCloudModal({ show: true, type, isEdit: true, accountId: account.id });
        } else {
            setCloudForm({});
            setCloudModal({ show: true, type, isEdit: false, accountId: null });
        }
        setCloudMsg({ success: '', error: '' });
    };

    const handleRescan = async (accountId, status) => {
        if (status?.toLowerCase() === 'running') {
            showToast("A scan is already in progress. Please wait for it to complete.", 'error');
            return;
        }

        try {
            const response = await rescanCloudAccountService(accountId);
            const resData = response?.statusCode || response?.data || response;

            if (resData?.status === 1) {
                showToast(resData?.response_data?.message || "Scan triggered successfully!", "success");
                fetchOverview();
            } else {
                showToast(resData?.error?.message || "Failed to trigger scan.", "error");
            }
        } catch (error) {
            showToast(error?.response?.data?.error?.message || "Something went wrong.", "error");
        }
    };

    const closeCloudModal = () => {
        setCloudModal({ show: false, type: '', isEdit: false, accountId: null });
        setCloudForm({});
        setCloudMsg({ success: '', error: '' });
    };

    const handleCloudSubmit = async () => {
        setCloudLoading(true);
        setCloudMsg({ success: '', error: '' });
        try {
            let response;
            const profile = JSON.parse(localStorage.getItem('user_profile') || '{}');
            const orgId = profile?.organization_id || profile?.org_id || '';

            if (cloudModal.type === 'aws') {
                if (cloudModal.isEdit && cloudModal.accountId) {
                    response = await editCloudAccountService(cloudModal.accountId, {
                        role_arn: cloudForm.arn || '',
                    });
                } else {
                    response = await addCloudAccountService({
                        role_arn: cloudForm.arn || '',
                        use_external_id: false,
                    });
                }
            } else if (cloudModal.type === 'azure') {
                response = await populateAzureDataService({
                    organization_id: orgId,
                    tenant_id: cloudForm.tenant_id || '',
                    client_id: cloudForm.client_id || '',
                    client_secret: cloudForm.client_secret || '',
                    subscription_id: cloudForm.subscription_id || '',
                });
            } else if (cloudModal.type === 'gcp') {
                response = await populateGcpDataService({
                    organization_id: orgId,
                    project_id: cloudForm.project_id || '',
                    target_service_account: cloudForm.target_service_account || '',
                });
            } else if (cloudModal.type === 'onprem') {
                const formData = new FormData();
                formData.append('organization_id', orgId);
                if (cloudForm.excel_file) {
                    formData.append('excel_file', cloudForm.excel_file);
                }
                response = await populateOnpremDataService(formData);
            }

            if (response?.statusCode?.status === 0) {
                const errMsg = response?.statusCode?.error?.message || 'Failed to submit data.';
                showToast(errMsg, 'error');
                return;
            }

            const msg = response?.statusCode?.response_data?.message || response?.data?.message || 'Data submitted successfully!';
            showToast(msg, 'success');
            fetchOverview(); // Refresh overview after success
            closeCloudModal(); // Close modal after success
        } catch (error) {
            const errMsg = error?.response?.data?.error?.message || error?.response?.data?.message || error?.message || 'Something went wrong.';
            showToast(errMsg, 'error');
        } finally {
            setCloudLoading(false);
        }
    };

    const content = (
        <div className={wrapper ? 'scroll_section' : ''}>
            <div className='d-flex align-items-center gap-3 mb-3'>
                <ModuleIcon name="integration" />
                <h3 className='color_white mb-0 font_20 font-600'>Integration Cloud</h3>
            </div>
            <div className="v2_card">
                <div className="integration-grid">
                    {integrations.map((item, idx) => {
                        const account = cloudAccounts.find(acc => acc.provider?.toLowerCase() === item.type?.toLowerCase());

                        return (
                            <div
                                key={idx}
                                className={`integration-card${item.type ? ' integration-card--clickable' : ''}${account ? ' integration-card--active' : ''}`}
                                style={{ '--accent': item.color }}
                                onClick={() => {
                                    if (account) {
                                        navigate('/cloud-compliance-details');
                                    } else if (item.type) {
                                        openCloudModal(item.type);
                                    }
                                }}
                            >
                                <div className="integration-card__header">
                                    <div className="integration-card__icon">
                                        {item.img ? (
                                            <img src={item.img} alt={item.name} style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                                        ) : (
                                            item.icon
                                        )}
                                    </div>
                                    {account && (
                                        <div className="d-flex align-items-center gap-2">

                                            <div className={`integration-card__status-badge status--${account.status?.toLowerCase()}`}>
                                                {account.status}
                                            </div>
                                            <button
                                                className="integration-card__rescan-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRescan(account.id, account.status);
                                                }}
                                                title="Trigger Rescan"
                                            >
                                                ↻ Rescan
                                            </button>
                                            <button
                                                className="integration-card__edit-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (account.status?.toLowerCase() === 'running') {
                                                        showToast("A scan is currently in progress. You cannot edit configuration at this time.", 'error');
                                                        return;
                                                    }
                                                    openCloudModal(item.type, account);
                                                }}
                                                title="Edit configuration"
                                            >
                                                ✎ Edit
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className="integration-card__body">
                                    <span className="integration-card__name">{item.name}</span>
                                    {account && (
                                        <div className="integration-card__meta">
                                            <span className="integration-card__arn" title={account.role_arn}>
                                                {account.role_arn ? (account.role_arn.length > 20 ? `${account.role_arn.substring(0, 20)}...` : account.role_arn) : 'No ARN'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Cloud Modal */}
            <Modal show={cloudModal.show} onHide={closeCloudModal} centered className="cstm_modal">
                <Modal.Header closeButton>
                    <Modal.Title>{cloudTitles[cloudModal.type]} — {cloudModal.isEdit ? 'Update' : 'Configuration'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="px-3 py-2">
                        {(cloudFields[cloudModal.type] || []).map((field) => (
                            <div key={field.key} className="mb-3">
                                <label className="form-label color_white" style={{ fontSize: '13px', fontWeight: '600' }}>
                                    {field.label} <span className="text-danger">*</span>
                                </label>
                                {field.type === 'file' ? (
                                    <input
                                        type="file"
                                        accept=".xlsx,.xls,.csv"
                                        className="form-control questionnaire_input"
                                        onChange={(e) => setCloudForm({ ...cloudForm, [field.key]: e.target.files[0] })}
                                    />
                                ) : field.type === 'checkbox' ? (
                                    <input
                                        type="checkbox"
                                        className="form-check-input ms-2"
                                        style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
                                        checked={!!cloudForm[field.key]}
                                        onChange={(e) => setCloudForm({ ...cloudForm, [field.key]: e.target.checked })}
                                    />
                                ) : (
                                    <input
                                        type={field.type}
                                        className="form-control questionnaire_input"
                                        placeholder={field.placeholder}
                                        value={cloudForm[field.key] || ''}
                                        onChange={(e) => setCloudForm({ ...cloudForm, [field.key]: e.target.value })}
                                    />
                                )}
                            </div>
                        ))}
                        {cloudMsg.success && <p className="text-success mt-2">{cloudMsg.success}</p>}
                        {cloudMsg.error && <p className="text-danger mt-2">{cloudMsg.error}</p>}
                        <div className='d-flex justify-content-end gap-3'>
                            <button className="btn btn-secondary" onClick={closeCloudModal}>Close</button>
                            <button
                                className="btn btn-primary"
                                onClick={handleCloudSubmit}
                                disabled={cloudLoading}
                            >
                                {cloudLoading ? 'Submitting...' : 'Submit'}
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Toast */}
            {toast.show && (
                <div className={`custom-toast ${toast.type}`}>
                    <span className="icon">
                        {toast.type === 'success' ? '✓' : '✕'}
                    </span>
                    <span className="message">{toast.message}</span>
                    <button
                        className="close-btn"
                        onClick={() => setToast({ show: false, message: '', type: 'success' })}
                    >
                        ✕
                    </button>
                </div>
            )}
        </div>
    );

    if (wrapper) {
        return <div className="cstm_dashboard_tabs p-4">{content}</div>;
    }

    return content;
}

export default IntegrationCloud;
